<template>
    <form-builder-next
        v-slot="{ formBuilder }"
        :form="FinmaReportForm"
        hide-default-buttons
        @submit="handleSubmit"
    >
        <card
            :columns="{ xs: 12 }"
            :heading="$tc('common.term.configuration')"
            class="q-mb-sm"
        >
            <div class="row q-col-gutter-md">
                <div class="col-xs-6 col-sm-4 col-md-3 col-lg-2">
                    <form-builder-next :item="formBuilder.form.getItem('dateFrom')" />
                </div>

                <div class="col-xs-6 col-sm-4 col-md-3 col-lg-2">
                    <form-builder-next :item="formBuilder.form.getItem('dateUntil')" />
                </div>

                <div v-if="currentPrefillYear" class="col-xs-12">
                    <base-button class="q-mr-sm" :label="$tc('common.time.year--previous-n-years', 1)" @click="setYearRange(currentPrefillYear)" />
                    <base-button :label="$tc('common.time.year--current-year', 1)" @click="setYearRange(currentPrefillYear + 1)" />
                </div>
            </div>
        </card>

        <div class="col-xs-12">
            <base-table
                ref="table"
                :columns="consultingCompaniesColumns"
                :fetch-objects-fn="ContactService.all"
                :additional-filters="additionalFilters"
                user-settings-base-path="FinmaReportConsultingCompanies"
                pagination-sort-by-default-key="contact_name"
                selection="multiple"
                pagination-initial-order-direction-ascending
                :selected.sync="selectedConsultingCompanies"
            >
                <template v-slot:tabs>
                    <base-toggle
                        v-model="includeInactive"
                        :label="$tc('common.reporting.report.include-inactive-consulting-companies', 1)"
                    />
                </template>

                <template v-slot:body-cell-contact_name="slotProps">
                    <q-td :props="slotProps" class="contact-name-td" data-test="text:contact-name">
                        <contact-name
                            :contact="slotProps.row"
                            highlighted
                            comma-separated
                        />
                    </q-td>
                </template>

                <template v-slot:body-cell-status="slotProps">
                    <q-td :props="slotProps" auto-width>
                        <status-badge :status="slotProps.row.consultingSettings.status" />
                    </q-td>
                </template>
            </base-table>
        </div>

        <in-page-footer>
            <base-button
                data-test="btn:submit"
                type="submit"
                :label="$tc('common.reporting.report.create-report--create-n-reports', selectedConsultingCompanies.length || 1)"
                :disable="disableSubmit"
                :loading="formBuilder.formSubmitStatus"
                primary-button
            />
        </in-page-footer>
    </form-builder-next>
</template>

<script>
import BaseTable from '@/components/BaseTable.vue'
import ContactName from '@/components/contact/ContactName.vue'
import BaseToggle from '@/components/form/BaseToggle.vue'
import StatusBadge from '@/components/StatusBadge.vue'
import { useI18n } from '@/composables/i18n'
import { ConsultingSettingsStatus, ContactType } from '@/enums/graphql'
import { FinmaReportForm } from '@/forms/reporting/finmaReport'
import { ContactService } from '@/services'
import { endOfYear, lightFormat, startOfYear, subYears } from 'date-fns'
import { computed, onMounted, ref } from 'vue'

export default {
    name: 'FinmaReportForm',
    components: { BaseToggle, ContactName, StatusBadge, BaseTable },
    setup (props, context) {
        const { tc } = useI18n()

        // Form
        const formComponent = ref(null)
        const disableSubmit = computed(() => {
            if (formComponent.value?.formBuilder.formSubmitStatus) return true
            if (formComponent.value?.formBuilder.form.hasError) return true
            if (!selectedConsultingCompanies.value.length) return true
            return false
        })
        function handleSubmit(formData) {
            context.emit('submit', Object.assign({
                consultingCompanyIds: selectedConsultingCompanies.value.map(consultingCompany => consultingCompany.id),
            }, formData))
        }

        // Prefill
        const currentPrefillYear = ref(null)
        function setCurrentPrefillYear (year) {
            currentPrefillYear.value = year
            setYearRange(year)
        }
        function setYearRange (year) {
            formComponent.value.formBuilder.form.getItem('dateFrom').value = lightFormat(startOfYear(new Date(year, 0, 1)), 'yyyy-MM-dd')
            formComponent.value.formBuilder.form.getItem('dateUntil').value = lightFormat(endOfYear(new Date(year, 11, 31)), 'yyyy-MM-dd')
        }
        onMounted(() => {
            setCurrentPrefillYear(subYears(new Date(), 1).getFullYear())
        })

        // ConsultingCompanies
        const includeInactive = ref(false)
        const selectedConsultingCompanies = ref([])
        const consultingCompaniesColumns = [
            {
                name: 'contact_name',
                label: tc('common.term.name'),
                sortable: true,
                align: 'left',
                required: true,
            },
            {
                name: 'status',
                label: tc('common.term.status'),
                align: 'left',
                required: true,
            },
        ]
        const additionalFilters = computed(() => {
            const consultingSettingsStatus = [ConsultingSettingsStatus.ACTIVE]
            if (includeInactive.value) consultingSettingsStatus.push(ConsultingSettingsStatus.INACTIVE)
            return {
                contact_type: ContactType.COMPANY,
                consultingSettingsStatus,
                hasConsultingSettings: true,
            }
        })
        function resetSelectConsultingCompanies () {
            selectedConsultingCompanies.value = []
        }

        return {
            // Form
            formComponent,
            FinmaReportForm,
            disableSubmit,
            handleSubmit,

            // Prefill
            currentPrefillYear,
            setYearRange,

            // ConsultingCompanies
            ContactService,
            includeInactive,
            selectedConsultingCompanies,
            consultingCompaniesColumns,
            additionalFilters,
            resetSelectConsultingCompanies,
        }
    },
}
</script>
